<template>
  <div class="whatsapp-admin">
    <n-tabs v-model:value="activeTab">
      <!-- WABA Accounts Tab -->
      <n-tab-pane name="wabas" tab="WABA Accounts">
        <n-card>
          <template #header>
            <div class="card-header">
              <span>WhatsApp Business Accounts</span>
              <n-button type="primary" @click="openWABAModal()">Add WABA</n-button>
            </div>
          </template>

          <n-data-table
            :columns="wabaColumns"
            :data="wabas"
            :loading="loading"
            :row-key="(row) => row.id"
            :expanded-row-keys="expandedWabas"
            @update:expanded-row-keys="expandedWabas = $event"
          />
        </n-card>
      </n-tab-pane>

      <!-- Business Access Tab -->
      <n-tab-pane name="businesses" tab="Business Access">
        <n-card>
          <template #header>
            <span>Business WABA & Phone Assignments</span>
          </template>

          <n-data-table
            :columns="businessColumns"
            :data="businessOverviews"
            :loading="loading"
            :row-key="(row) => row.business_id"
          />
        </n-card>
      </n-tab-pane>

      <!-- Analytics Tab -->
      <n-tab-pane name="analytics" tab="Analytics">
        <n-card>
          <template #header>
            <div class="card-header">
              <span>Message Analytics</span>
              <n-space>
                <n-date-picker
                  v-model:value="analyticsDateRange"
                  type="daterange"
                  clearable
                  @update:value="loadAnalytics"
                />
              </n-space>
            </div>
          </template>

          <div v-if="analyticsSummary" class="analytics-grid">
            <n-card class="stat-card">
              <n-statistic label="Total Sent" :value="analyticsSummary.total_sent" />
            </n-card>
            <n-card class="stat-card">
              <n-statistic label="Delivered" :value="analyticsSummary.total_delivered" />
            </n-card>
            <n-card class="stat-card">
              <n-statistic label="Read" :value="analyticsSummary.total_read" />
            </n-card>
            <n-card class="stat-card">
              <n-statistic label="Failed" :value="analyticsSummary.total_failed" />
            </n-card>
            <n-card class="stat-card">
              <n-statistic label="Delivery Rate" :value="`${(analyticsSummary.delivery_rate * 100).toFixed(1)}%`" />
            </n-card>
            <n-card class="stat-card">
              <n-statistic label="Read Rate" :value="`${(analyticsSummary.read_rate * 100).toFixed(1)}%`" />
            </n-card>
          </div>
        </n-card>
      </n-tab-pane>

      <!-- Message Logs Tab -->
      <n-tab-pane name="logs" tab="Message Logs">
        <n-card>
          <template #header>
            <div class="card-header">
              <span>Message History</span>
              <n-space>
                <n-select
                  v-model:value="logFilters.status"
                  :options="statusOptions"
                  placeholder="Status"
                  clearable
                  style="width: 120px"
                />
                <n-date-picker
                  v-model:value="logDateRange"
                  type="daterange"
                  clearable
                />
                <n-button @click="loadMessageLogs">Search</n-button>
              </n-space>
            </div>
          </template>

          <n-data-table
            :columns="logColumns"
            :data="messageLogs"
            :loading="loading"
            :row-key="(row) => row.id"
            :pagination="{ pageSize: 20 }"
          />
        </n-card>
      </n-tab-pane>
    </n-tabs>

    <!-- WABA Create/Edit Modal -->
    <n-modal v-model:show="showWABAModal" preset="dialog" :title="editingWABA ? 'Edit WABA' : 'Add WABA Account'">
      <n-form :model="wabaForm" label-placement="left" label-width="140">
        <n-form-item label="Display Name" required>
          <n-input v-model:value="wabaForm.name" placeholder="e.g., OnePlaceHQ Primary" />
        </n-form-item>
        <n-form-item label="WABA ID" required>
          <n-input v-model:value="wabaForm.waba_id" placeholder="WhatsApp Business Account ID" :disabled="!!editingWABA" />
        </n-form-item>
        <n-form-item v-if="!editingWABA" label="Access Token" required>
          <n-input
            v-model:value="wabaForm.access_token"
            type="password"
            show-password-on="click"
            placeholder="System User Access Token"
          />
        </n-form-item>
        <n-form-item v-if="editingWABA" label="Access Token">
          <n-input
            v-model:value="wabaForm.access_token"
            type="password"
            show-password-on="click"
            placeholder="Leave empty to keep existing"
          />
        </n-form-item>
        <n-form-item label="Meta App ID">
          <n-input v-model:value="wabaForm.meta_app_id" placeholder="Optional" />
        </n-form-item>
        <n-form-item v-if="editingWABA" label="Active">
          <n-switch v-model:value="wabaForm.is_active" />
        </n-form-item>
      </n-form>
      <template #action>
        <n-button @click="showWABAModal = false">Cancel</n-button>
        <n-button type="primary" :loading="saving" @click="saveWABA">
          {{ editingWABA ? 'Update' : 'Create' }}
        </n-button>
      </template>
    </n-modal>

    <!-- Update Token Modal (Quick access) -->
    <n-modal v-model:show="showTokenModal" preset="dialog" title="Update Access Token">
      <n-alert v-if="tokenWABA" type="info" :bordered="false" style="margin-bottom: 16px">
        Updating token for: <strong>{{ tokenWABA.name }}</strong>
        <br />
        <small>Current token: {{ tokenWABA.access_token_masked || '••••' }}</small>
      </n-alert>
      <n-form label-placement="left" label-width="120">
        <n-form-item label="New Token" required>
          <n-input
            v-model:value="newAccessToken"
            type="password"
            show-password-on="click"
            placeholder="Paste your new System User Access Token"
          />
        </n-form-item>
      </n-form>
      <n-alert type="warning" :bordered="false" style="margin-top: 12px">
        <small>Get a new token from <a href="https://business.facebook.com/settings/system-users" target="_blank">Meta Business Settings → System Users</a></small>
      </n-alert>
      <template #action>
        <n-button @click="showTokenModal = false">Cancel</n-button>
        <n-button type="primary" :loading="saving" @click="saveNewToken">Update Token</n-button>
      </template>
    </n-modal>

    <!-- Business Assignment Modal -->
    <n-modal v-model:show="showAssignModal" preset="dialog" title="Assign WABA to Business">
      <n-form label-placement="left" label-width="120">
        <n-form-item label="WABA Account">
          <n-select
            v-model:value="assignForm.waba_account_id"
            :options="wabaOptions"
            placeholder="Select WABA"
          />
        </n-form-item>
        <n-form-item label="Default">
          <n-switch v-model:value="assignForm.is_default" />
        </n-form-item>
      </n-form>
      <template #action>
        <n-button @click="showAssignModal = false">Cancel</n-button>
        <n-button type="primary" :loading="saving" @click="assignWABA">Assign</n-button>
      </template>
    </n-modal>

    <!-- Phone Assignment Modal -->
    <n-modal v-model:show="showPhoneAssignModal" preset="dialog" title="Assign Phone to Business">
      <n-form label-placement="left" label-width="120">
        <n-form-item label="Phone Number">
          <n-select
            v-model:value="phoneAssignForm.waba_phone_number_id"
            :options="availablePhoneOptions"
            placeholder="Select Phone"
          />
        </n-form-item>
        <n-form-item label="Primary">
          <n-switch v-model:value="phoneAssignForm.is_primary" />
        </n-form-item>
        <n-form-item label="Priority">
          <n-input-number v-model:value="phoneAssignForm.priority" :min="0" />
        </n-form-item>
      </n-form>
      <template #action>
        <n-button @click="showPhoneAssignModal = false">Cancel</n-button>
        <n-button type="primary" :loading="saving" @click="assignPhone">Assign</n-button>
      </template>
    </n-modal>

    <!-- Phone Numbers Drawer -->
    <n-drawer v-model:show="showPhonesDrawer" :width="600" placement="right">
      <n-drawer-content :title="`Phone Numbers - ${selectedWABA?.name || ''}`">
        <n-space vertical>
          <n-button type="primary" size="small" @click="syncPhoneNumbers">Sync from Meta</n-button>
          <n-data-table
            :columns="phoneColumns"
            :data="selectedPhones"
            :loading="loading"
            :row-key="(row) => row.id"
            size="small"
          />
        </n-space>
      </n-drawer-content>
    </n-drawer>

    <!-- Templates Drawer -->
    <n-drawer v-model:show="showTemplatesDrawer" :width="700" placement="right">
      <n-drawer-content :title="`Templates - ${selectedWABA?.name || ''}`">
        <n-space vertical>
          <n-button type="primary" size="small" @click="syncTemplates">Sync from Meta</n-button>
          <n-data-table
            :columns="templateColumns"
            :data="selectedTemplates"
            :loading="loading"
            :row-key="(row) => row.id"
            size="small"
          />
        </n-space>
      </n-drawer-content>
    </n-drawer>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, h } from 'vue'
import {
  NTabs,
  NTabPane,
  NCard,
  NDataTable,
  NButton,
  NSpace,
  NModal,
  NForm,
  NFormItem,
  NInput,
  NInputNumber,
  NSelect,
  NSwitch,
  NTag,
  NStatistic,
  NDatePicker,
  NDrawer,
  NDrawerContent,
  NAlert,
  useDialog,
} from 'naive-ui'
import { useToast } from '@shared/composables/useToast.js'
import { useWhatsAppAdmin } from '../composables/useWhatsAppAdmin'

const toast = useToast()
const dialog = useDialog()

const {
  loading,
  listWABAAccounts,
  createWABAAccount,
  updateWABAAccount,
  deleteWABAAccount,
  testWABAConnection,
  syncWABA,
  listPhoneNumbers,
  syncPhoneNumbers: syncPhonesAPI,
  listTemplates,
  syncTemplates: syncTemplatesAPI,
  listBusinessOverviews,
  listBusinessWABAs,
  assignWABAToBusiness,
  removeWABAFromBusiness,
  listBusinessPhones,
  assignPhoneToBusiness,
  removePhoneFromBusiness,
  getAnalyticsSummary,
  listMessageLogs,
} = useWhatsAppAdmin()

const activeTab = ref('wabas')
const saving = ref(false)

// WABA State
const wabas = ref([])
const expandedWabas = ref([])
const showWABAModal = ref(false)
const editingWABA = ref(null)
const wabaForm = ref({
  name: '',
  waba_id: '',
  access_token: '',
  meta_app_id: '',
  is_active: true,
})

// Token Update Modal State
const showTokenModal = ref(false)
const tokenWABA = ref(null)
const newAccessToken = ref('')

// Phone/Template Drawers
const showPhonesDrawer = ref(false)
const showTemplatesDrawer = ref(false)
const selectedWABA = ref(null)
const selectedPhones = ref([])
const selectedTemplates = ref([])

// Business State
const businessOverviews = ref([])
const showAssignModal = ref(false)
const selectedBusinessId = ref(null)
const assignForm = ref({
  waba_account_id: null,
  is_default: false,
})

// Phone Assignment
const showPhoneAssignModal = ref(false)
const availablePhones = ref([])
const phoneAssignForm = ref({
  waba_phone_number_id: null,
  is_primary: false,
  priority: 0,
})

// Analytics State
const analyticsSummary = ref(null)
const analyticsDateRange = ref(null)

// Message Logs State
const messageLogs = ref([])
const logFilters = ref({
  status: null,
})
const logDateRange = ref(null)

const statusOptions = [
  { label: 'Pending', value: 'pending' },
  { label: 'Sent', value: 'sent' },
  { label: 'Delivered', value: 'delivered' },
  { label: 'Read', value: 'read' },
  { label: 'Failed', value: 'failed' },
]

const wabaOptions = computed(() =>
  wabas.value.map((w) => ({ label: w.name, value: w.id }))
)

const availablePhoneOptions = computed(() =>
  availablePhones.value.map((p) => ({
    label: `${p.phone_number} (${p.display_name || 'No name'})`,
    value: p.id,
  }))
)

// WABA Columns
const wabaColumns = [
  {
    type: 'expand',
    renderExpand: (row) => {
      return h('div', { style: 'padding: 12px' }, [
        h(NSpace, { vertical: true }, () => [
          h('div', {}, `WABA ID: ${row.waba_id}`),
          h('div', {}, `Token: ${row.access_token_masked || '••••'}`),
          h('div', {}, `Last Synced: ${row.last_synced_at ? new Date(row.last_synced_at).toLocaleString('en-IN') : 'Never'}`),
        ]),
      ])
    },
  },
  { title: 'ID', key: 'id', width: 60 },
  { title: 'Name', key: 'name' },
  {
    title: 'Status',
    key: 'is_active',
    width: 100,
    render: (row) =>
      h(NTag, { type: row.is_active ? 'success' : 'error', size: 'small' }, () =>
        row.is_active ? 'Active' : 'Inactive'
      ),
  },
  {
    title: 'Actions',
    key: 'actions',
    width: 400,
    render: (row) =>
      h(NSpace, { size: 'small' }, () => [
        h(NButton, { size: 'small', onClick: () => openPhones(row) }, () => 'Phones'),
        h(NButton, { size: 'small', onClick: () => openTemplates(row) }, () => 'Templates'),
        h(NButton, { size: 'small', type: 'info', onClick: () => testConnection(row) }, () => 'Test'),
        h(NButton, { size: 'small', type: 'warning', onClick: () => syncWABAData(row) }, () => 'Sync'),
        h(NButton, { size: 'small', type: 'primary', onClick: () => openTokenModal(row) }, () => 'Token'),
        h(NButton, { size: 'small', onClick: () => openWABAModal(row) }, () => 'Edit'),
        h(NButton, { size: 'small', type: 'error', onClick: () => confirmDeleteWABA(row) }, () => 'Delete'),
      ]),
  },
]

// Phone Columns
const phoneColumns = [
  { title: 'ID', key: 'id', width: 60 },
  { title: 'Number', key: 'phone_number' },
  { title: 'Display Name', key: 'display_name' },
  {
    title: 'Quality',
    key: 'quality_rating',
    width: 80,
    render: (row) => {
      const colors = { GREEN: 'success', YELLOW: 'warning', RED: 'error' }
      return row.quality_rating
        ? h(NTag, { type: colors[row.quality_rating] || 'default', size: 'small' }, () => row.quality_rating)
        : '-'
    },
  },
  {
    title: 'Status',
    key: 'status',
    width: 100,
    render: (row) =>
      h(NTag, { type: row.status === 'active' ? 'success' : 'error', size: 'small' }, () => row.status),
  },
]

// Template Columns
const templateColumns = [
  { title: 'Name', key: 'name' },
  { title: 'Language', key: 'language', width: 80 },
  {
    title: 'Category',
    key: 'category',
    width: 100,
    render: (row) => h(NTag, { size: 'small' }, () => row.category),
  },
  {
    title: 'Status',
    key: 'status',
    width: 100,
    render: (row) => {
      const colors = { APPROVED: 'success', PENDING: 'warning', REJECTED: 'error', DISABLED: 'default' }
      return h(NTag, { type: colors[row.status] || 'default', size: 'small' }, () => row.status)
    },
  },
  { title: 'Local Mapping', key: 'local_template_name', width: 120 },
]

// Business Columns
const businessColumns = [
  { title: 'ID', key: 'business_id', width: 60 },
  { title: 'Business', key: 'business_name' },
  { title: 'WABAs', key: 'waba_count', width: 80 },
  { title: 'Phones', key: 'phone_count', width: 80 },
  { title: 'Primary Phone', key: 'primary_phone', width: 140 },
  { title: 'Default WABA', key: 'default_waba_name', width: 150 },
  { title: 'Messages (30d)', key: 'messages_last_30d', width: 120 },
  {
    title: 'Actions',
    key: 'actions',
    width: 200,
    render: (row) =>
      h(NSpace, { size: 'small' }, () => [
        h(NButton, { size: 'small', onClick: () => openAssignWABA(row.business_id) }, () => 'Assign WABA'),
        h(NButton, { size: 'small', onClick: () => openAssignPhone(row.business_id) }, () => 'Assign Phone'),
      ]),
  },
]

// Log Columns
const logColumns = [
  { title: 'ID', key: 'id', width: 60 },
  { title: 'Business', key: 'business_name', width: 120 },
  { title: 'Customer', key: 'customer_phone', width: 120 },
  { title: 'Template', key: 'template_name', width: 120 },
  {
    title: 'Status',
    key: 'status',
    width: 100,
    render: (row) => {
      const colors = {
        pending: 'default',
        sent: 'info',
        delivered: 'success',
        read: 'success',
        failed: 'error',
      }
      return h(NTag, { type: colors[row.status] || 'default', size: 'small' }, () => row.status)
    },
  },
  {
    title: 'Sent At',
    key: 'sent_at',
    width: 160,
    render: (row) => (row.sent_at ? new Date(row.sent_at).toLocaleString('en-IN') : '-'),
  },
  { title: 'Phone Used', key: 'phone_number_used', width: 120 },
]

// WABA Methods
const loadWABAs = async () => {
  try {
    wabas.value = await listWABAAccounts()
  } catch (e) {
    toast.error('Failed to load WABA accounts')
  }
}

const openWABAModal = (waba = null) => {
  editingWABA.value = waba
  if (waba) {
    wabaForm.value = {
      name: waba.name,
      waba_id: waba.waba_id,
      access_token: '',
      meta_app_id: waba.meta_app_id || '',
      is_active: waba.is_active,
    }
  } else {
    wabaForm.value = {
      name: '',
      waba_id: '',
      access_token: '',
      meta_app_id: '',
      is_active: true,
    }
  }
  showWABAModal.value = true
}

const saveWABA = async () => {
  saving.value = true
  try {
    if (editingWABA.value) {
      const data = { name: wabaForm.value.name, is_active: wabaForm.value.is_active }
      if (wabaForm.value.access_token) data.access_token = wabaForm.value.access_token
      if (wabaForm.value.meta_app_id) data.meta_app_id = wabaForm.value.meta_app_id
      await updateWABAAccount(editingWABA.value.id, data)
      toast.success('WABA updated')
    } else {
      await createWABAAccount(wabaForm.value)
      toast.success('WABA created')
    }
    showWABAModal.value = false
    loadWABAs()
  } catch (e) {
    toast.error(e.message)
  } finally {
    saving.value = false
  }
}

const confirmDeleteWABA = (waba) => {
  dialog.warning({
    title: 'Delete WABA',
    content: `Are you sure you want to delete "${waba.name}"? This will also remove all phone numbers and template mappings.`,
    positiveText: 'Delete',
    negativeText: 'Cancel',
    onPositiveClick: async () => {
      try {
        await deleteWABAAccount(waba.id)
        toast.success('WABA deleted')
        loadWABAs()
      } catch (e) {
        toast.error(e.message)
      }
    },
  })
}

const testConnection = async (waba) => {
  try {
    const result = await testWABAConnection(waba.id)
    if (result.success) {
      toast.success(`Connection OK! ${result.details?.join(', ') || ''}`)
    } else {
      toast.error(`Connection failed: ${result.error}`)
    }
  } catch (e) {
    toast.error(e.message)
  }
}

const syncWABAData = async (waba) => {
  try {
    const result = await syncWABA(waba.id)
    toast.success(`Synced ${result.phone_numbers_synced} phones, ${result.templates_synced} templates`)
    loadWABAs()
  } catch (e) {
    toast.error(e.message)
  }
}

// Token Update Methods
const openTokenModal = (waba) => {
  tokenWABA.value = waba
  newAccessToken.value = ''
  showTokenModal.value = true
}

const saveNewToken = async () => {
  if (!newAccessToken.value) {
    toast.error('Please enter a new access token')
    return
  }
  saving.value = true
  try {
    await updateWABAAccount(tokenWABA.value.id, { access_token: newAccessToken.value })
    toast.success('Access token updated successfully')
    showTokenModal.value = false
    newAccessToken.value = ''
    loadWABAs()
  } catch (e) {
    toast.error(e.message)
  } finally {
    saving.value = false
  }
}

// Phone/Template Drawers
const openPhones = async (waba) => {
  selectedWABA.value = waba
  try {
    selectedPhones.value = await listPhoneNumbers(waba.id)
    showPhonesDrawer.value = true
  } catch (e) {
    toast.error(e.message)
  }
}

const openTemplates = async (waba) => {
  selectedWABA.value = waba
  try {
    selectedTemplates.value = await listTemplates(waba.id)
    showTemplatesDrawer.value = true
  } catch (e) {
    toast.error(e.message)
  }
}

const syncPhoneNumbers = async () => {
  if (!selectedWABA.value) return
  try {
    const result = await syncPhonesAPI(selectedWABA.value.id)
    toast.success(`Synced ${result.synced} phone numbers`)
    selectedPhones.value = await listPhoneNumbers(selectedWABA.value.id)
  } catch (e) {
    toast.error(e.message)
  }
}

const syncTemplates = async () => {
  if (!selectedWABA.value) return
  try {
    const result = await syncTemplatesAPI(selectedWABA.value.id)
    toast.success(`Synced ${result.synced} templates`)
    selectedTemplates.value = await listTemplates(selectedWABA.value.id)
  } catch (e) {
    toast.error(e.message)
  }
}

// Business Methods
const loadBusinessOverviews = async () => {
  try {
    businessOverviews.value = await listBusinessOverviews()
  } catch (e) {
    toast.error('Failed to load business overviews')
  }
}

const openAssignWABA = (businessId) => {
  selectedBusinessId.value = businessId
  assignForm.value = { waba_account_id: null, is_default: false }
  showAssignModal.value = true
}

const assignWABA = async () => {
  if (!assignForm.value.waba_account_id) {
    toast.error('Please select a WABA')
    return
  }
  saving.value = true
  try {
    await assignWABAToBusiness(selectedBusinessId.value, assignForm.value)
    toast.success('WABA assigned')
    showAssignModal.value = false
    loadBusinessOverviews()
  } catch (e) {
    toast.error(e.message)
  } finally {
    saving.value = false
  }
}

const openAssignPhone = async (businessId) => {
  selectedBusinessId.value = businessId
  phoneAssignForm.value = { waba_phone_number_id: null, is_primary: false, priority: 0 }

  // Load available phones from all WABAs
  availablePhones.value = []
  for (const waba of wabas.value) {
    try {
      const phones = await listPhoneNumbers(waba.id)
      availablePhones.value.push(...phones)
    } catch (e) {
      // Ignore
    }
  }
  showPhoneAssignModal.value = true
}

const assignPhone = async () => {
  if (!phoneAssignForm.value.waba_phone_number_id) {
    toast.error('Please select a phone')
    return
  }
  saving.value = true
  try {
    await assignPhoneToBusiness(selectedBusinessId.value, phoneAssignForm.value)
    toast.success('Phone assigned')
    showPhoneAssignModal.value = false
    loadBusinessOverviews()
  } catch (e) {
    toast.error(e.message)
  } finally {
    saving.value = false
  }
}

// Analytics Methods
const loadAnalytics = async () => {
  try {
    const params = {}
    if (analyticsDateRange.value) {
      params.date_from = new Date(analyticsDateRange.value[0]).toISOString().split('T')[0]
      params.date_to = new Date(analyticsDateRange.value[1]).toISOString().split('T')[0]
    }
    analyticsSummary.value = await getAnalyticsSummary(params)
  } catch (e) {
    toast.error('Failed to load analytics')
  }
}

// Message Log Methods
const loadMessageLogs = async () => {
  try {
    const params = { limit: 100 }
    if (logFilters.value.status) params.status = logFilters.value.status
    if (logDateRange.value) {
      params.date_from = new Date(logDateRange.value[0]).toISOString().split('T')[0]
      params.date_to = new Date(logDateRange.value[1]).toISOString().split('T')[0]
    }
    const result = await listMessageLogs(params)
    messageLogs.value = result.data || result
  } catch (e) {
    toast.error('Failed to load message logs')
  }
}

onMounted(() => {
  loadWABAs()
  loadBusinessOverviews()
  loadAnalytics()
  loadMessageLogs()
})
</script>

<style scoped>
.whatsapp-admin {
  max-width: 1400px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.analytics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
}

.stat-card {
  text-align: center;
}
</style>
